import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const EMPTY = { name: "", category: "Candles", type: "physical", price: 0, description: "", image: "", stock: 100, featured: false, active: true, digital_file_path: null, digital_filename: null };
const input = "w-full bg-surface border border-gold/20 rounded-xl px-4 py-2.5 text-cream placeholder:text-faded focus:outline-none focus:ring-1 focus:ring-gold text-sm";

export default function AdminProducts() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(EMPTY);
    const [editId, setEditId] = useState(null);
    const [uploading, setUploading] = useState(false);

    const load = () => api.get("/products").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const uploadDigital = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setForm((f) => ({ ...f, digital_file_path: data.path, digital_filename: data.filename }));
            toast.success("File uploaded");
        } catch { toast.error("Upload failed"); }
        setUploading(false);
    };

    const save = async (e) => {
        e.preventDefault();
        const payload = { ...form, price: parseFloat(form.price), stock: parseInt(form.stock) };
        try {
            if (editId) await api.put(`/admin/products/${editId}`, payload);
            else await api.post("/admin/products", payload);
            toast.success("Saved");
            setOpen(false); setForm(EMPTY); setEditId(null); load();
        } catch { toast.error("Could not save"); }
    };
    const edit = (p) => { setForm(p); setEditId(p.id); setOpen(true); };
    const del = async (id) => { if (!window.confirm("Delete this product?")) return; await api.delete(`/admin/products/${id}`); toast.success("Deleted"); load(); };

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <h1 className="font-serif text-4xl text-cream">Products</h1>
                <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(EMPTY); setEditId(null); } }}>
                    <DialogTrigger asChild>
                        <button data-testid="add-product-btn" className="inline-flex items-center gap-2 bg-gold text-ink rounded-full px-5 py-2.5 text-sm font-medium hover:bg-goldbright transition-colors"><Plus className="w-4 h-4" /> New Product</button>
                    </DialogTrigger>
                    <DialogContent className="bg-surface2 border-gold/20 text-cream max-w-lg max-h-[90vh] overflow-y-auto">
                        <DialogHeader><DialogTitle className="font-serif text-2xl">{editId ? "Edit" : "New"} Product</DialogTitle></DialogHeader>
                        <form onSubmit={save} className="space-y-3" data-testid="product-form">
                            <input required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} />
                            <div className="grid grid-cols-2 gap-3">
                                <input placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={input} />
                                <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={input}>
                                    <option value="physical">Physical</option><option value="digital">Digital</option>
                                </select>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <input type="number" step="0.01" placeholder="Price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={input} />
                                <input type="number" placeholder="Stock" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className={input} />
                            </div>
                            <input placeholder="Image URL" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className={input} />
                            {form.type === "digital" && (
                                <div className="rounded-xl border border-gold/20 bg-surface p-3">
                                    <div className="text-xs text-ash mb-2">Digital file (delivered after purchase)</div>
                                    <input type="file" onChange={(e) => uploadDigital(e.target.files?.[0])} className="text-xs text-ash file:mr-3 file:rounded-full file:border-0 file:bg-gold file:text-ink file:px-3 file:py-1.5 file:text-xs" data-testid="digital-upload" />
                                    {uploading && <div className="text-xs text-gold mt-2">Uploading…</div>}
                                    {form.digital_filename && !uploading && <div className="text-xs text-gold mt-2">Attached: {form.digital_filename}</div>}
                                </div>
                            )}
                            <textarea rows={3} placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={input} />
                            <div className="flex gap-6 text-sm text-ash">
                                <label className="flex items-center gap-2"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="accent-gold" /> Featured</label>
                                <label className="flex items-center gap-2"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-gold" /> Active</label>
                            </div>
                            <button className="w-full bg-gold text-ink rounded-full py-2.5 font-medium" data-testid="product-save">Save Product</button>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            <div className="rounded-2xl border border-gold/10 overflow-hidden" data-testid="products-table">
                <table className="w-full text-sm">
                    <thead className="bg-surface2 text-faded text-left"><tr><th className="p-4">Product</th><th className="p-4">Category</th><th className="p-4">Price</th><th className="p-4">Stock</th><th className="p-4"></th></tr></thead>
                    <tbody>
                        {items.map((p) => (
                            <tr key={p.id} className="border-t border-gold/10">
                                <td className="p-4 flex items-center gap-3"><img src={p.image} alt="" className="w-10 h-10 rounded object-cover" /><span className="text-cream">{p.name}</span></td>
                                <td className="p-4 text-ash">{p.category}</td>
                                <td className="p-4 text-gold">${p.price.toFixed(2)}</td>
                                <td className="p-4 text-ash">{p.stock}</td>
                                <td className="p-4 text-right whitespace-nowrap">
                                    <button onClick={() => edit(p)} className="text-ash hover:text-gold mr-3"><Pencil className="w-4 h-4 inline" /></button>
                                    <button onClick={() => del(p.id)} className="text-ash hover:text-clay"><Trash2 className="w-4 h-4 inline" /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
